<script lang="ts">
    import { PLAY_STATES } from '@amp/web-app-components/src/constants';

    import type { IconState } from '@amp/web-app-components/src/components/buttons/PlayButton/constants';

    /**
     * playing bars animation to show
     *
     * @type {PLAYING, PAUSE}
     */
    export let iconState: IconState;
    /**
     * should playing bars trigger refocus animation
     */
    export let isRefocused: boolean;
    /**
     * should playing bars should trigger not in focus animation
     */
    export let isWindowNotFocused: boolean;

    const MAX = 10000;
    const MIN = 1;

    const id = Math.floor(Math.random() * (MAX - MIN) + MIN);
    const { PLAYING, PAUSE } = PLAY_STATES;
</script>

<div
    aria-hidden="true"
    class="playback-bars"
    class:playing={iconState === PLAYING}
    class:paused={iconState === PAUSE}
    class:is-refocused={isRefocused}
    class:is-not-focused={isWindowNotFocused}
    data-testid="playback-bars"
>
    <svg class="playback-bars__svg icon" viewBox="0 0 11 11">
        <defs>
            <rect
                id={`bar-${id}`}
                x="0"
                width="2.1"
                y="0"
                height="11"
                rx=".25"
            />
            <mask id={`bar-mask-${id}`}>
                <use href={`#bar-${id}`} fill="white" />
            </mask>
        </defs>
        <g mask={`url(#bar-mask-${id})`}>
            <use
                class="playback-bars__bar playback-bars__bar--1"
                href={`#bar-${id}`}
            />
        </g>
        <g mask={`url(#bar-mask-${id})`} transform="translate(2.9668 0)">
            <use
                class="playback-bars__bar playback-bars__bar--2"
                href={`#bar-${id}`}
            />
        </g>
        <g mask={`url(#bar-mask-${id})`} transform="translate(5.9333 0)">
            <use
                class="playback-bars__bar playback-bars__bar--3"
                href={`#bar-${id}`}
            />
        </g>
        <g mask={`url(#bar-mask-${id})`} transform="translate(8.8999 0)">
            <use
                class="playback-bars__bar playback-bars__bar--4"
                href={`#bar-${id}`}
            />
        </g>
    </svg>
    {#if $$slots['hover-slot']}
        <div class="playing-bars-hover">
            <slot name="hover-slot" />
        </div>
    {/if}
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    .playback-bars {
        $default-height: 15px;
        width: 100%;
        height: $default-height;
        margin: 0 auto;
        pointer-events: none;

        @include absolute-center;

        .search-list-lockup & {
            // Scale play button sizing to size of artwork in each viewport
            @media (--medium) {
                height: calc(#{$default-height} * 1.225);
            }

            @media (--xlarge) {
                height: calc(#{$default-height} * 1.275);
            }
        }
    }

    .playback-bars__svg {
        @include absolute-center;

        .playing & {
            will-change: transform;
            z-index: var(--z-gpu);
        }
    }

    ///
    /// Playback bar initial translateY
    ///
    /// @type Number
    ///
    $playback-bar-initial-translateY: 9px;

    // Bars are built in an SVG: `rect` with rounded corners, animated by translating down inside an identical mask,
    // changing "height" while preserving border-radius (unlike scale). All bar (rect) transform values are in `px`:
    // browsers calculate `px` according to SVG's internal coordinate system, regardless of rendered size.
    .playback-bars__bar {
        // 9px = 11px (height of rect) - 2px (lowest height)
        // All rects default to 2px high (paused state)
        transform: translateY($playback-bar-initial-translateY);
        animation-name: none;
        animation-duration: 400ms;
        animation-delay: var(--delay, 0);
        animation-direction: alternate;
        animation-timing-function: ease-in;
        animation-iteration-count: infinite;
        fill: var(--playingBarColor, var(--keyColor));

        .artwork-overlay &,
        .selected & {
            fill: white;
        }

        .playing & {
            animation-name: playback-bar;
            will-change: transform;
            z-index: var(--z-gpu);
        }

        .playing.is-not-focused & {
            // Advance bars to inactive "iconic" state, when window is not in focus
            // This saves a lot of energy and improves performance
            animation-name: playback-bar-iconic;
            animation-iteration-count: 1;
            animation-fill-mode: forwards;
            animation-delay: 0;
        }

        // Resume continuous animation after backing out of "iconic" state, when window regains focus
        // Relies on `is-refocused` class being removed from `<body>` after `600ms`
        .playing.is-refocused & {
            transform: translateY(var(--iconic-height-offset));
            animation-name: playback-bar-refocus, playback-bar;
            animation-iteration-count: 1, infinite;
            animation-timing-function: linear;
            animation-delay: 0;
        }
    }

    // `--max-height-offset` = distance from rect's max-height.
    // `--delay` = values to create non-repeating animations
    .playback-bars__bar--1 {
        --max-height-offset: 6px;
        --delay: 250ms;
        // Set a mid-point for inactive window animation
        --iconic-midpoint-height-offset: 4px;
        // Reset to iconic positioning when window is not focused
        --iconic-height-offset: 7px;
        // Slow animation for a short bar
        animation-duration: 600ms;
    }

    .playback-bars__bar--2 {
        --max-height-offset: 2px;
        --delay: 615ms;
        // Set a mid-point for inactive window animation
        --iconic-midpoint-height-offset: 2px;
        // Reset to iconic positioning when window is not focused
        --iconic-height-offset: 1px;
    }

    .playback-bars__bar--3 {
        --max-height-offset: 4px;
        --delay: 475ms;
        // Set a mid-point for inactive window animation
        --iconic-midpoint-height-offset: 4px;
        // Reset to iconic positioning when window is not focused
        --iconic-height-offset: 3px;
    }

    .playback-bars__bar--4 {
        --max-height-offset: 2px;
        --delay: 25ms;
        // Set a mid-point for inactive window animation
        --iconic-midpoint-height-offset: 5px;
        // Reset to iconic positioning when window is not focused
        --iconic-height-offset: 6px;
    }

    // Playback bar "playing" animation
    @keyframes playback-bar {
        from {
            // Safari won't recover from re-focus animation without this line
            transform: translateY($playback-bar-initial-translateY);
        }

        to {
            transform: translateY(var(--max-height-offset));
        }
    }

    // Animation to advance to "iconic" state when the window is inactive
    @keyframes playback-bar-iconic {
        0% {
            transform: translateY($playback-bar-initial-translateY);
        }

        50% {
            transform: translateY(var(--iconic-midpoint-height-offset));
        }

        100% {
            transform: translateY(var(--iconic-height-offset));
        }
    }

    // Animation to back out of "iconic" state, when window regains focus
    @keyframes playback-bar-refocus {
        0% {
            transform: translateY(var(--iconic-height-offset));
        }

        100% {
            transform: translateY(var(--max-height-offset));
        }
    }

    .playing-bars-hover {
        opacity: 0;

        @include absolute-center;
    }
</style>
